import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Printer, Download, X, Zap, Loader2 } from 'lucide-react';
import { openPrintWindow } from '@/pages/owner/OwnerPrintTemplates';

/**
 * Modal pratinjau dokumen sebelum cetak/download.
 * Mendukung 2 mode: 'pdf' (iframe) & 'dotmatrix' (monospace text).
 *
 * Props:
 *   - open, onClose
 *   - mode: 'pdf' | 'dotmatrix'
 *   - pdfBlob: Blob (untuk pdf mode)
 *   - filename: string (untuk download)
 *   - dmText: string (untuk dotmatrix)
 *   - dmOrientation: 'portrait' | 'landscape'
 *   - title: string (header modal)
 */
export default function PrintPreviewModal({
  open,
  onClose,
  mode,
  pdfBlob,
  filename,
  dmText,
  dmOrientation = 'portrait',
  title = 'Pratinjau Dokumen',
}) {
  const [pdfUrl, setPdfUrl] = useState(null);

  useEffect(() => {
    if (open && mode === 'pdf' && pdfBlob) {
      const url = URL.createObjectURL(pdfBlob);
      setPdfUrl(url);
      return () => {
        URL.revokeObjectURL(url);
        setPdfUrl(null);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, pdfBlob]);

  const doDownload = () => {
    if (mode === 'pdf' && pdfBlob) {
      const url = URL.createObjectURL(pdfBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'dokumen.pdf';
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  const doPrint = () => {
    if (mode === 'dotmatrix' && dmText) {
      openPrintWindow(dmText, filename || 'Dokumen', dmOrientation);
    } else if (mode === 'pdf' && pdfUrl) {
      // Buka PDF di tab baru dan otomatis panggil print
      const w = window.open(pdfUrl, '_blank');
      if (w) {
        setTimeout(() => { try { w.print(); } catch (_) {} }, 600);
      }
    }
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh] p-0 flex flex-col" data-testid="print-preview-modal">
        <DialogHeader className="px-4 pt-4 pb-2 shrink-0">
          <DialogTitle className="flex items-center gap-2 text-base">
            <span>{title}</span>
            {mode === 'dotmatrix' && (
              <Badge variant="outline" className="text-[10px]">
                <Zap className="h-2.5 w-2.5 mr-0.5" /> Dot Matrix
              </Badge>
            )}
            {mode === 'pdf' && (
              <Badge variant="outline" className="text-[10px]">PDF</Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-hidden border-y">
          {mode === 'pdf' && pdfUrl ? (
            <iframe
              src={pdfUrl}
              className="w-full h-full"
              style={{ minHeight: '60vh' }}
              title="PDF Preview"
              data-testid="print-preview-pdf-iframe"
            />
          ) : mode === 'dotmatrix' && dmText ? (
            <div
              className="w-full h-full overflow-auto bg-zinc-950 text-zinc-100 p-4"
              style={{ minHeight: '60vh' }}
              data-testid="print-preview-dm-content"
            >
              <pre className="font-mono text-[11px] leading-tight whitespace-pre">{dmText}</pre>
            </div>
          ) : (
            <div className="flex items-center justify-center h-64 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin mr-2" /> Memuat pratinjau...
            </div>
          )}
        </div>

        <DialogFooter className="px-4 py-3 shrink-0 gap-2 sm:gap-2">
          <Button variant="outline" onClick={onClose} data-testid="print-preview-close">
            <X className="h-4 w-4 mr-1" /> Batal
          </Button>
          {mode === 'pdf' && (
            <Button variant="outline" onClick={doDownload} data-testid="print-preview-download">
              <Download className="h-4 w-4 mr-1" /> Unduh PDF
            </Button>
          )}
          <Button onClick={doPrint} data-testid="print-preview-print">
            <Printer className="h-4 w-4 mr-1" /> Cetak Sekarang
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
